import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import uploadService from "./uploadService"; // Ensure correct import path

// Upload file thunk
export const uploadAFile = createAsyncThunk(
  'upload/files',
  async (files, thunkAPI) => {
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));

      // Function to handle the progress event
      const handleProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        thunkAPI.dispatch(updateUploadProgress(percentCompleted)); // Dispatch the progress update
      };

      // Call the uploadService to upload the file, passing the handleProgress function
      const uploadedFiles = await uploadService.uploadFile(formData, handleProgress);
      return uploadedFiles; // Return the uploaded files data to the reducer
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.message,
        response: error.response?.data || null,
      });
    }
  }
);



// Delete file thunk
export const deleteAFile = createAsyncThunk(
  'delete/file',
  async (id, thunkAPI) => {
    try {
      // Delete file using the service
      await uploadService.deleteFile(id);
      return id; // Return the file ID for the reducer to filter it out
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.message,
        response: error.response?.data || null,
      });
    }
  }
);

const initialState = {
  files: [], // Array to hold uploaded files
  uploading: false,
  uploadProgress: 0,
  isError: false,
  isLoading: false,
  isSuccess: false,
  message: "", // For error or success messages
};

// Slice to handle the file upload logic
export const uploadSlice = createSlice({
  name: 'files',
  initialState,
  reducers: {
    updateUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle upload actions
      .addCase(uploadAFile.pending, (state) => {
        state.isLoading = true;
        state.uploading = true;
        state.uploadProgress = 0; // Reset progress on start
      })
      .addCase(uploadAFile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.uploading = false;  // Ensure `uploading` is set to false once the upload finishes
        state.isSuccess = true;
        state.files = [...state.files, ...action.payload];
        state.uploadProgress = 100;
      })
      .addCase(uploadAFile.rejected, (state, action) => {
        state.isLoading = false;
        state.uploading = false;
        state.isError = true;
        state.message = action.payload?.message || 'Upload failed';
        state.uploadProgress = 0; // Reset progress on failure
      })
      // Handle delete actions
      .addCase(deleteAFile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteAFile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        // Remove deleted file from the state
        state.files = state.files.filter(file => file.public_id !== action.payload);
      })
      .addCase(deleteAFile.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload?.message || 'Delete failed';
      });
  },
});

export default uploadSlice.reducer;
export const { updateUploadProgress } = uploadSlice.actions;